import { Link, createFileRoute } from '@tanstack/react-router';
import { CapabilitiesCard } from '../components/lxc/CapabilitiesCard';
import { ContainerCard, useContainerDetail } from '../components/lxc/ContainerCard';
import { GatewayBindsCard } from '../components/lxc/GatewayBindsCard';
import { CreateContainer } from '../components/lxc/CreateContainer';
import { DeleteContainer } from '../components/lxc/DeleteContainer';
import { ContainersBrowser } from '../components/lxc/ContainersBrowser';

/**
 * A container view for one Proxmox VMID. Deliberately three independent
 * queries rather than one combined fetch: `getLXC`, `getLXCCapabilities` and
 * `listLXCGatewayBinds` are unrelated dependencies on the backend (owner
 * directory, Proxmox, pfSense), and a 503 from one must not blank the panels
 * that already loaded fine — see each card component for its own error
 * handling.
 */

/**
 * `vmid` lives in the query string rather than as a path segment. The route
 * tree is generated (`src/routeTree.gen.ts`) and shared with work happening
 * elsewhere in this repo right now, so this screen stays a single static
 * file — `/lxc` — and gets its shareable-link requirement from `?vmid=`
 * instead of a `/lxc/$vmid` path param that would need the tree regenerated.
 */
interface LxcSearch {
  vmid?: string;
}

export const Route = createFileRoute('/lxc')({
  // Omit the key entirely rather than setting it to `undefined` —
  // `exactOptionalPropertyTypes` treats those as different things, and only
  // the omitted form matches `vmid?: string`.
  validateSearch: (search: Record<string, unknown>): LxcSearch =>
    typeof search.vmid === 'string' && search.vmid.length > 0 ? { vmid: search.vmid } : {},
  component: LxcScreen,
});

/**
 * openapi.json spells the vmid path parameter out as an 8-branch regex:
 * a positive decimal integer, no leading zero, capped at
 * Number.MAX_SAFE_INTEGER (branched that way so the bound can be checked
 * without floating-point math server-side). Number.isSafeInteger says the
 * same thing in one comparison. Checked here so a malformed value shows as a
 * local message instead of spending a round trip to get back the API's own
 * 400 `invalid-vmid`.
 */
function isValidVmid(raw: string): boolean {
  return /^[1-9][0-9]*$/.test(raw) && Number.isSafeInteger(Number(raw));
}

function LxcScreen() {
  const search = Route.useSearch();
  const vmid = search.vmid;
  const vmidValid = vmid !== undefined && isValidVmid(vmid);

  return (
    <section>
      <h1>Containers</h1>

      {vmid !== undefined && (
        <p>
          <Link to="/lxc" search={{}}>
            ← All containers
          </Link>
        </p>
      )}

      {vmid === undefined && (
        <>
          <ContainersBrowser />
          <CreateContainer />
        </>
      )}

      {vmid !== undefined && !vmidValid && (
        <p role="alert">
          &quot;{vmid}&quot; is not a valid VMID. It must be a positive integer with no leading
          zero, no greater than {Number.MAX_SAFE_INTEGER}.
        </p>
      )}

      {vmid !== undefined && vmidValid && <ContainerPanels key={vmid} vmid={vmid} />}
    </section>
  );
}

/**
 * The four panels for one container.
 *
 * Its own component so the container read can be a hook here rather than a
 * conditional one in `LxcScreen`. `useContainerDetail` is the same query
 * `ContainerCard` runs — same key, same fetcher — so this shares that panel's
 * cache entry and its single request rather than adding one.
 *
 * What this screen needs from it is `found`. `ContainerCard`, `CapabilitiesCard`
 * and `GatewayBindsCard` each already render "No container is registered under
 * VMID X" for a `found: false` response; `DeleteContainer` did not, so the
 * screen said the container did not exist three times over and then offered a
 * button to destroy it. The `key` stays on this whole subtree rather than on
 * the delete card alone, so a half-typed confirmation cannot survive a move to
 * a different VMID.
 */
function ContainerPanels({ vmid }: { vmid: string }) {
  const detail = useContainerDetail(vmid);

  return (
    <>
      <ContainerCard vmid={vmid} />
      <CapabilitiesCard vmid={vmid} />
      <GatewayBindsCard vmid={vmid} />
      {/* Last, and after the reads: deletion should be reached by
          scrolling past the state it destroys, not sat beside the
          power buttons. */}
      <DeleteContainer vmid={vmid} found={detail.data?.found} />
    </>
  );
}

